/**
 * Billing Manager Component
 * Displays subscription and billing information with access to Stripe Customer Portal
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Chip,
  Divider,
  Alert,
  CircularProgress,
  Card,
  CardContent
} from '@mui/material';
import {
  CreditCard as CreditCardIcon,
  Receipt as ReceiptIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Launch as LaunchIcon
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { apiGet, apiPost } from '../../utils/apiUtils';

/**
 * BillingManager - Main billing management component
 */
const BillingManager = ({ onClose, onOpenSubscription }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [redirectingToPortal, setRedirectingToPortal] = useState(false);

  useEffect(() => {
    if (user) {
      fetchBillingData();
    }
  }, [user]);

  const fetchBillingData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch current subscription
      const subResponse = await apiGet('/api/stripe/subscription');
      if (subResponse.ok) {
        const subData = await subResponse.json();
        setSubscription(subData.subscription);
      }
    } catch (error) {
      console.error('Error fetching billing data:', error);
      setError('Failed to load billing information');
    } finally {
      setLoading(false);
    }
  };

  const handleManageBilling = async () => {
    try {
      setRedirectingToPortal(true);
      setError(null);

      const response = await apiPost('/api/stripe/create-portal-session', {});

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create billing portal session');
      }

      const data = await response.json();

      // Redirect to Stripe Customer Portal
      window.location.href = data.url;
    } catch (error) {
      console.error('Error creating portal session:', error);
      setError(error.message || 'Failed to open billing portal');
      setRedirectingToPortal(false);
    }
  };

  const getTierDisplayName = (tier) => {
    if (!tier) return 'Free';
    return tier.charAt(0).toUpperCase() + tier.slice(1);
  };

  const getTierColor = (tier) => {
    const colorMap = {
      basic: '#0095FF',
      pro: '#faa32c',
      organization: '#0095FF',
      free: '#808080'
    };
    return colorMap[tier?.toLowerCase()] || '#808080';
  };

  const getCreditsForTier = (tier) => {
    const creditsMap = {
      basic: '2,000',
      pro: '10,000',
      organization: '30,000',
      free: '100'
    };
    return creditsMap[tier?.toLowerCase()] || '100';
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp * 1000);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { label: 'Active', color: '#0095FF' },
      trialing: { label: 'Trial', color: '#faa32c' },
      past_due: { label: 'Past Due', color: '#FF1493' },
      canceled: { label: 'Canceled', color: '#808080' },
      incomplete: { label: 'Incomplete', color: '#FF1493' }
    };

    const config = statusConfig[status] || { label: 'Free', color: '#808080' };

    return (
      <Chip
        label={config.label}
        size="small"
        sx={{
          backgroundColor: `${config.color}20`,
          color: config.color,
          borderColor: config.color,
          border: '1px solid',
          fontWeight: 'bold'
        }}
      />
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
      </Box>
    );
  }

  const isFreeTier = !subscription || subscription.status !== 'active';
  const tier = subscription?.tier || 'free';

  return (
    <Box sx={{
      width: '100%',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      bgcolor: 'background.default',
      p: 0,
      m: 0
    }}>
      {/* Header */}
      <Box sx={{
        p: 3,
        borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <Typography variant="h4" sx={{ color: '#0095FF', fontWeight: 600 }}>
          Billing & Subscription
        </Typography>
        {onClose && (
          <Button
            onClick={onClose}
            variant="outlined"
            sx={{
              color: 'rgba(255, 255, 255, 0.7)',
              borderColor: 'rgba(255, 255, 255, 0.3)',
              '&:hover': {
                color: '#0095FF',
                borderColor: '#0095FF',
                backgroundColor: 'rgba(0, 149, 255, 0.1)'
              }
            }}
          >
            Close
          </Button>
        )}
      </Box>

      {/* Scrollable Content */}
      <Box sx={{
        flex: 1,
        overflow: 'auto',
        p: 3,
        display: 'flex',
        flexDirection: 'column',
        gap: 3
      }}>
        {/* Error Message */}
        {error && (
          <Alert
            severity="error"
            onClose={() => setError(null)}
            sx={{
              backgroundColor: 'rgba(255, 20, 147, 0.1)',
              border: '1px solid rgba(255, 20, 147, 0.3)',
              color: '#FF1493',
              '& .MuiAlert-icon': { color: '#FF1493' }
            }}
          >
            {error}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Current Subscription Card */}
          <Grid item xs={12} sm={6}>
            <Card sx={{
              backgroundColor: 'rgba(255, 255, 255, 0.02)',
              border: `2px solid ${getTierColor(tier)}`,
              borderRadius: 3,
              height: '100%'
            }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <CheckCircleIcon sx={{ color: getTierColor(tier), mr: 1 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                    Current Plan
                  </Typography>
                </Box>

                <Box sx={{ mb: 3 }}>
                  <Typography variant="h4" sx={{
                    fontWeight: 'bold',
                    color: getTierColor(tier),
                    mb: 1
                  }}>
                    {getTierDisplayName(tier)}
                  </Typography>
                  {getStatusBadge(subscription?.status || 'free')}
                </Box>

                <Divider sx={{ my: 2 }} />

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Credits per month
                    </Typography>
                    <Typography variant="h6" color="text.primary">
                      {getCreditsForTier(tier)}
                    </Typography>
                  </Box>

                  {subscription && subscription.status === 'active' && (
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Next billing date
                      </Typography>
                      <Typography variant="body1" color="text.primary">
                        {formatDate(subscription.stripe?.current_period_end || subscription.current_period_end)}
                      </Typography>
                    </Box>
                  )}
                </Box>

                <Button
                  variant="outlined"
                  fullWidth
                  onClick={onOpenSubscription}
                  sx={{
                    mt: 3,
                    color: 'rgba(255, 255, 255, 0.7)',
                    borderColor: 'rgba(255, 255, 255, 0.3)',
                    '&:hover': {
                      color: '#0095FF',
                      borderColor: '#0095FF',
                      backgroundColor: 'rgba(0, 149, 255, 0.1)'
                    }
                  }}
                >
                  Change Plan
                </Button>
              </CardContent>
            </Card>
          </Grid>

          {/* Billing Management Card */}
          <Grid item xs={12} sm={6}>
            <Card sx={{
              backgroundColor: 'rgba(255, 255, 255, 0.02)',
              border: '1px solid rgba(255, 255, 255, 0.05)',
              borderRadius: 3,
              height: '100%'
            }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <CreditCardIcon sx={{ color: '#0095FF', mr: 1 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                    Billing Management
                  </Typography>
                </Box>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Manage your payment methods, view invoices, and update billing information through our secure billing portal.
                </Typography>

                {isFreeTier ? (
                  <Alert severity="info" sx={{
                    backgroundColor: 'rgba(0, 149, 255, 0.1)',
                    border: '1px solid rgba(0, 149, 255, 0.3)',
                    color: '#0095FF',
                    mb: 2,
                    '& .MuiAlert-icon': { color: '#0095FF' }
                  }}>
                    Subscribe to a paid plan to access billing management features.
                  </Alert>
                ) : (
                  <>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <CheckCircleIcon sx={{ fontSize: 16, color: '#0095FF', mr: 1 }} />
                        <Typography variant="body2" color="text.primary">
                          Update payment methods
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <ReceiptIcon sx={{ fontSize: 16, color: '#0095FF', mr: 1 }} />
                        <Typography variant="body2" color="text.primary">
                          View & download invoices
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <ScheduleIcon sx={{ fontSize: 16, color: '#0095FF', mr: 1 }} />
                        <Typography variant="body2" color="text.primary">
                          Manage subscription settings
                        </Typography>
                      </Box>
                    </Box>

                    <Button
                      variant="contained"
                      fullWidth
                      endIcon={redirectingToPortal ? <CircularProgress size={20} color="inherit" /> : <LaunchIcon />}
                      onClick={handleManageBilling}
                      disabled={redirectingToPortal}
                      sx={{
                        backgroundColor: '#0095FF',
                        color: 'white',
                        '&:hover': {
                          backgroundColor: '#0077CC',
                        },
                        '&:disabled': {
                          backgroundColor: 'rgba(0, 149, 255, 0.3)',
                          color: 'rgba(255, 255, 255, 0.5)'
                        }
                      }}
                    >
                      {redirectingToPortal ? 'Opening Portal...' : 'Manage Billing'}
                    </Button>
                  </>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default BillingManager;
